import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { COLORS } from "@/constants/colors";
import type {
  ColorScaleMethod,
  ColorScaleSettings,
  CustomCategoryValue,
  CustomRangeValue,
  Dataset,
  DatasetData,
  DatasetExploreItem,
  DatasetFilter,
  DatasetHistory,
  DatasetIndices,
  DatasetsMap,
  EColorLabel,
  EColorNames,
  H3Configuration,
  SingleStyleValue,
  WeightValueEntry,
  WeightValues
} from "@/types/dataset";
import { getColorScaleMap } from "@/utils/color";
import {
  performCalculatePercentagesFromExistingWeights,
  performRecalculateAllPercentagesV2,
  recalculateWeightValuesPreservingZScores
} from "@/utils/weightValues";

export interface DatasetState {
  /** Store object that keeps all the datasets being used for analysis which includes both the available and deleted datasets */
  datasets: DatasetsMap;
  filters: {
    [datasetId: string]: DatasetFilter[];
  };
  rawDatasets: {
    [id: string]: DatasetData;
  };
  visualizationOrder: string[];
  selectedDataset: Dataset | null;
  administrativeData: string[];
  datasetIndices: DatasetIndices;
  weightValues: WeightValues; // Weight values for spatial analysis
  history: DatasetHistory;
  analysisId: string | null;
  selectedDatasetExplorer: DatasetExploreItem | null;
  /** Snapshot of weighted dataset ids when the weight config was last saved or
   * loaded. Null means no saved baseline exists. Warning state is derived from
   * this snapshot and the current weight values by selectors. */
  weightConfigBaseline: string[] | null;
}

export const datasetInitialState: DatasetState = {
  datasets: {},
  filters: {},
  visualizationOrder: [],
  rawDatasets: {},
  selectedDataset: null,
  administrativeData: [],
  datasetIndices: {},
  weightValues: {},
  history: {
    datasets: {},
    visualizationOrder: [],
    rawDatasets: {}
  },
  analysisId: null,
  selectedDatasetExplorer: null,
  weightConfigBaseline: null
};

/** Remembers what a Color Scale method was left at, so switching away and back
 * restores it. Only the given parts are updated. */
const rememberColorScaleSettings = (
  style: Dataset["style"],
  method: ColorScaleMethod | undefined,
  settings: ColorScaleSettings
) => {
  if (!method) return;
  style.colorScaleSettings = {
    ...style.colorScaleSettings,
    [method]: { ...style.colorScaleSettings?.[method], ...settings }
  };
};

export const datasetSlice = createSlice({
  name: "dataset",
  initialState: datasetInitialState,
  reducers: {
    addRawDataset: (
      state,
      action: PayloadAction<{ dataset: DatasetData; id: string }>
    ) => {
      const { dataset, id } = action.payload;

      state.rawDatasets[id] = {
        ...dataset,
        display_name: dataset.display_name ?? dataset?.name // TODO: remove when BE has returned display_name
      };
    },
    updateRawDatasetDisplayName: (
      state,
      action: PayloadAction<{ datasetId: string; displayName: string }>
    ) => {
      const { datasetId, displayName } = action.payload;
      if (state.rawDatasets[datasetId]) {
        state.rawDatasets[datasetId].display_name = displayName;
      }
    },
    removeRawDataset: (state, action: PayloadAction<string>) => {
      const datasetId = action.payload;
      delete state.rawDatasets[datasetId];
    },
    clearRawDatasets: (state) => {
      state.rawDatasets = {};
    },
    setAnalysisId: (state, action: PayloadAction<string | null>) => {
      state.analysisId = action.payload;
    },
    addDataset: (
      state,
      // Ugly solutin for calculate weight values, but the fatest solution
      action: PayloadAction<{ dataset: Dataset; isCalculateWeight?: boolean }>
    ) => {
      const { dataset, isCalculateWeight } = action.payload;

      state.datasets[dataset.data.id] = {
        ...dataset,
        data: {
          ...dataset.data,
          display_name: dataset.data.display_name ?? dataset.data.name // TODO: remove when BE has returned display_name
        }
      };

      // Ensure no duplicates in visualizationOrder
      if (!state.visualizationOrder.includes(dataset.data.id)) {
        state.visualizationOrder = Array.isArray(state.visualizationOrder)
          ? [dataset.data.id, ...state.visualizationOrder]
          : [dataset.data.id];
      }

      if (isCalculateWeight) {
        recalculateWeightValuesPreservingZScores(state);
      }
    },
    updateDatasetDisplayName: (
      state,
      action: PayloadAction<{
        datasetId: string;
        displayName: string;
      }>
    ) => {
      const { datasetId, displayName } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].data.display_name = displayName;
      }
    },
    updateDatasetH3Config: (
      state,
      action: PayloadAction<{
        datasetId: string;
        config: H3Configuration | null;
      }>
    ) => {
      const { datasetId, config } = action.payload;
      state.datasets[datasetId].h3Configuration = config;
    },
    updateDatasetStyle: (
      state,
      action: PayloadAction<{
        datasetId: string;
        style: Dataset["style"];
      }>
    ) => {
      const { datasetId, style } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style = style;
      }
    },

    updateDatasetStyleVisibleLabel: (
      state,
      action: PayloadAction<{
        datasetId: string;
        visible: boolean;
      }>
    ) => {
      const { datasetId, visible } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.labelConfig.show_label = visible;
      }
    },

    updateDatasetStyleAttributeLabel: (
      state,
      action: PayloadAction<{
        datasetId: string;
        attribute_name: string;
        attribute_id: string;
      }>
    ) => {
      const { datasetId, attribute_id, attribute_name } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.labelConfig.attribute_id = attribute_id;
        state.datasets[datasetId].style.labelConfig.attribute_name =
          attribute_name;
      }
    },

    updateDatasetStyleColorLabel: (
      state,
      action: PayloadAction<{
        datasetId: string;
        color: EColorLabel;
      }>
    ) => {
      const { datasetId, color } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.labelConfig.color_scale = color;
      }
    },

    updateDatasetStyleOpacityLabel: (
      state,
      action: PayloadAction<{
        datasetId: string;
        opacity: number;
      }>
    ) => {
      const { datasetId, opacity } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.labelConfig.opacity = opacity;
      }
    },

    updateDatasetColorScaleMethod: (
      state,
      action: PayloadAction<{
        datasetId: string;
        method: ColorScaleMethod;
      }>
    ) => {
      const { datasetId, method } = action.payload;

      if (state.datasets[datasetId]) {
        const style = state.datasets[datasetId].style;
        // Restore this method's own palette (per-method color scale). Falls back
        // to the current active palette the first time a method is used. An
        // UNCONFIRMED Custom Ordinal (auto-seed, not user-saved) has no palette
        // of its own yet — it follows the current active palette (e.g. Ordinal's)
        // rather than restoring a stale per-method value.
        const restored =
          method === "custom_ordinal" && !style.customCategoryConfigSaved
            ? style.colorFamily
            : (style.colorScaleSettings?.[method]?.colorFamily ??
              style.colorFamily);
        style.colorScaleMethod = method;
        style.colorFamily = restored;

        // Restore this method's own opacity, or keep the current one the first time
        // it is used. Lines carry it on the stroke, other geometries on the fill.
        const isLine =
          state.datasets[datasetId].properties.spatialType === "line";
        const currentOpacity = isLine
          ? style.stroke.opacity
          : style.fillOpacity;
        const restoredOpacity =
          style.colorScaleSettings?.[method]?.opacity ?? currentOpacity;
        if (isLine) style.stroke.opacity = restoredOpacity;
        else style.fillOpacity = restoredOpacity;
        rememberColorScaleSettings(style, method, {
          colorFamily: restored,
          opacity: restoredOpacity
        });
      }
    },

    /** Stores the step count the user picked for one Color Scale method, so it
     * survives a method switch and a reopen. */
    updateDatasetColorScaleSteps: (
      state,
      action: PayloadAction<{
        datasetId: string;
        method: ColorScaleMethod;
        steps: number;
      }>
    ) => {
      const { datasetId, method, steps } = action.payload;

      if (state.datasets[datasetId]) {
        const style = state.datasets[datasetId].style;
        rememberColorScaleSettings(style, method, { steps });
      }
    },

    // Live preview: update only the rendered config so the map/legend reflect
    // in-progress panel edits; the saved baseline stays put for Cancel.
    previewDatasetSingleStyleConfig: (
      state,
      action: PayloadAction<{
        datasetId: string;
        singleStyleConfig: SingleStyleValue;
      }>
    ) => {
      const { datasetId, singleStyleConfig } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) dataset.style.singleStyleConfig = singleStyleConfig;
    },

    // Commit: promote the config to both the live and saved slots — panel Save,
    // and the first-time default seed (so opening the panel isn't already dirty).
    commitDatasetSingleStyleConfig: (
      state,
      action: PayloadAction<{
        datasetId: string;
        singleStyleConfig: SingleStyleValue;
      }>
    ) => {
      const { datasetId, singleStyleConfig } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) {
        dataset.style.singleStyleConfig = singleStyleConfig;
        dataset.style.singleStyleConfigSaved = singleStyleConfig;
      }
    },

    // Cancel: revert the live config to the last saved baseline.
    cancelDatasetSingleStyleConfig: (
      state,
      action: PayloadAction<{ datasetId: string }>
    ) => {
      const dataset = state.datasets[action.payload.datasetId];
      if (dataset?.style.singleStyleConfigSaved) {
        dataset.style.singleStyleConfig = dataset.style.singleStyleConfigSaved;
      }
    },

    // Live preview: update only the rendered config (panel color/value edits);
    // the saved baseline stays put for Cancel.
    previewDatasetCustomCategoryConfig: (
      state,
      action: PayloadAction<{
        datasetId: string;
        customCategoryConfig: CustomCategoryValue;
      }>
    ) => {
      const { datasetId, customCategoryConfig } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) dataset.style.customCategoryConfig = customCategoryConfig;
    },

    // Commit (panel Save): promote the config to both slots. Its presence in the
    // saved slot marks the config as user-saved ("confirmed").
    commitDatasetCustomCategoryConfig: (
      state,
      action: PayloadAction<{
        datasetId: string;
        customCategoryConfig: CustomCategoryValue;
      }>
    ) => {
      const { datasetId, customCategoryConfig } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) {
        dataset.style.customCategoryConfig = customCategoryConfig;
        dataset.style.customCategoryConfigSaved = customCategoryConfig;
      }
    },

    // Seed (auto-seed / reset): set the live config and CLEAR the saved slot, so
    // the config stays unconfirmed and keeps following the active color scale.
    seedDatasetCustomCategoryConfig: (
      state,
      action: PayloadAction<{
        datasetId: string;
        customCategoryConfig: CustomCategoryValue;
      }>
    ) => {
      const { datasetId, customCategoryConfig } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) {
        dataset.style.customCategoryConfig = customCategoryConfig;
        dataset.style.customCategoryConfigSaved = undefined;
      }
    },

    // In-place committed change (palette / step count / filter reconcile): update
    // the live config, and keep the saved baseline in sync ONLY when the config
    // is already confirmed — an unconfirmed auto-seed must stay unconfirmed.
    syncDatasetCustomCategoryConfig: (
      state,
      action: PayloadAction<{
        datasetId: string;
        customCategoryConfig: CustomCategoryValue;
      }>
    ) => {
      const { datasetId, customCategoryConfig } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) {
        dataset.style.customCategoryConfig = customCategoryConfig;
        if (dataset.style.customCategoryConfigSaved) {
          dataset.style.customCategoryConfigSaved = customCategoryConfig;
        }
      }
    },

    // Cancel: revert the live config to the last saved baseline. When nothing is
    // saved yet (unconfirmed), the caller reseeds instead (see useDataset).
    cancelDatasetCustomCategoryConfig: (
      state,
      action: PayloadAction<{ datasetId: string }>
    ) => {
      const dataset = state.datasets[action.payload.datasetId];
      if (dataset?.style.customCategoryConfigSaved) {
        dataset.style.customCategoryConfig =
          dataset.style.customCategoryConfigSaved;
      }
    },

    // Live preview: update only the rendered config so the map/legend reflect
    // in-progress panel edits; the saved baseline stays put for Cancel.
    previewDatasetCustomRangeConfig: (
      state,
      action: PayloadAction<{
        datasetId: string;
        customRangeConfig: CustomRangeValue;
      }>
    ) => {
      const { datasetId, customRangeConfig } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) dataset.style.customRangeConfig = customRangeConfig;
    },

    // Commit: promote the config to both the live and saved slots — panel Save
    // and every committed flow (default seed, palette recolor, step redistribute,
    // reset, reconcile) so the panel isn't left dirty after an external change.
    commitDatasetCustomRangeConfig: (
      state,
      action: PayloadAction<{
        datasetId: string;
        customRangeConfig: CustomRangeValue;
      }>
    ) => {
      const { datasetId, customRangeConfig } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) {
        dataset.style.customRangeConfig = customRangeConfig;
        dataset.style.customRangeConfigSaved = customRangeConfig;
      }
    },

    // Cancel: revert the live config to the last saved baseline.
    cancelDatasetCustomRangeConfig: (
      state,
      action: PayloadAction<{ datasetId: string }>
    ) => {
      const dataset = state.datasets[action.payload.datasetId];
      if (dataset?.style.customRangeConfigSaved) {
        dataset.style.customRangeConfig = dataset.style.customRangeConfigSaved;
      }
    },

    // Stamp resolved asset ids onto legend image rows after a successful upload
    // (ORB-3333), so the SAME image isn't re-uploaded on the next save. Fills
    // `imageAssetId` (by matching `imageUrl`) on every dataset's Simple / Custom
    // Ordinal / Custom Breaks config — both the live and saved slots — but only
    // where an id isn't already set.
    applyLegendImageAssetIds: (
      state,
      action: PayloadAction<{ assetIdByImageUrl: Record<string, string> }>
    ) => {
      const { assetIdByImageUrl } = action.payload;
      if (!Object.keys(assetIdByImageUrl).length) return;
      const stamp = (
        row?: {
          imageUrl: string | null;
          imageAssetId: string | null;
        } | null
      ) => {
        if (row?.imageUrl && !row.imageAssetId) {
          const id = assetIdByImageUrl[row.imageUrl];
          if (id) row.imageAssetId = id;
        }
      };
      Object.values(state.datasets).forEach((dataset) => {
        const style = dataset?.style;
        if (!style) return;
        stamp(style.singleStyleConfig);
        stamp(style.singleStyleConfigSaved);
        style.customCategoryConfig?.steps.forEach(stamp);
        style.customCategoryConfigSaved?.steps.forEach(stamp);
        style.customRangeConfig?.steps.forEach(stamp);
        style.customRangeConfigSaved?.steps.forEach(stamp);
      });
    },

    updateDatasetColorStyle: (
      state,
      action: PayloadAction<{
        datasetId: string;
        colorFamily: EColorNames;
      }>
    ) => {
      const { datasetId, colorFamily } = action.payload;
      if (state.datasets[datasetId]) {
        const currentStyle = state.datasets[datasetId].style;

        const colorRanges = currentStyle.colorRanges?.map((range, index) => ({
          ...range,
          color: String(
            COLORS[colorFamily][
              getColorScaleMap(colorFamily, currentStyle.colorSteps || 10)[
                index
              ]
            ]
          )
        }));

        // Remember this palette for the ACTIVE method so switching away and back
        // restores it (per-method color scale).
        const method = currentStyle.colorScaleMethod;
        state.datasets[datasetId].style = {
          ...currentStyle,
          colorFamily,
          colorRanges
        };
        rememberColorScaleSettings(state.datasets[datasetId].style, method, {
          colorFamily
        });
      }
    },

    updateDatasetStyleEnabledRank: (
      state,
      action: PayloadAction<{
        datasetId: string;
        isEnabled: boolean;
      }>
    ) => {
      const { datasetId, isEnabled } = action.payload;

      if (state.datasets[datasetId]?.style?.rankConfig) {
        state.datasets[datasetId].style.rankConfig.isEnabled = isEnabled;
      }
    },

    updateDatasetStyleMaxRanking: (
      state,
      action: PayloadAction<{
        datasetId: string;
        maxRanking: number;
      }>
    ) => {
      const { datasetId, maxRanking } = action.payload;

      if (state.datasets[datasetId]?.style?.rankConfig) {
        state.datasets[datasetId].style.rankConfig.maxRanking = maxRanking;
      }
    },

    addAdministrativeData: (state, action: PayloadAction<string>) => {
      const datasetId = action.payload;

      state.administrativeData = Array.isArray(state.administrativeData)
        ? [...state.administrativeData, datasetId]
        : [datasetId];
    },
    setSelectedDataset: (state, action: PayloadAction<Dataset>) => {
      state.selectedDataset = action.payload;
    },
    clearSelectedDataset: (state) => {
      state.selectedDataset = null;
    },
    setDatasetIsFetching: (
      state,
      action: PayloadAction<{ datasetId: string; isFetching: boolean }>
    ) => {
      const { datasetId, isFetching } = action.payload;
      state.datasets[datasetId].properties.isFetching = isFetching;
    },
    updateDatasetPublishedStatus: (
      state,
      action: PayloadAction<{ datasetId: string; published: boolean }>
    ) => {
      const { datasetId, published } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].data.published = published;
        state.datasets[datasetId].data.available = published;
      }
    },
    removeDataset: (state, action: PayloadAction<string>) => {
      const datasetId = action.payload;
      const [baseId, indexStr] = datasetId.split("_");
      const index = Number.parseInt(indexStr);

      delete state.datasets[datasetId];
      delete state.filters[datasetId];

      state.visualizationOrder = state.visualizationOrder.filter(
        (id) => id !== datasetId
      );

      state.administrativeData = state.administrativeData.filter(
        (id) => id !== datasetId
      );

      // Update indices tracking
      if (state.datasetIndices[baseId]) {
        state.datasetIndices[baseId].usedIndices = state.datasetIndices[
          baseId
        ].usedIndices.filter((i) => i !== index);

        // If no more datasets with this base ID exist, clean up
        if (state.datasetIndices[baseId].usedIndices.length === 0) {
          delete state.datasetIndices[baseId];
        }
      }

      recalculateWeightValuesPreservingZScores(state);

      // Removing the last dataset voids the saved weight baseline: the saved
      // config's datasets are all gone, so a fresh dataset added later starts
      // clean and must not inherit the stale auto-adjusted warning.
      if (Object.keys(state.datasets).length === 0) {
        state.weightConfigBaseline = null;
      }
    },
    clearDataset: {
      reducer: (
        state,
        action: PayloadAction<{
          preserveWeightConfigBaseline?: boolean;
        }>
      ) => {
        state.datasets = {};
        state.visualizationOrder = [];
        state.rawDatasets = {};
        // A normal clear invalidates the saved baseline. Analysis results can
        // opt out because they temporarily replace the input datasets and
        // the baseline is needed again when the user clicks "Edit input".
        if (!action.payload?.preserveWeightConfigBaseline) {
          state.weightConfigBaseline = null;
        }
      },
      prepare: (options: { preserveWeightConfigBaseline?: boolean } = {}) => ({
        payload: options
      })
    },
    setDatasetHistory: (
      state,
      action: PayloadAction<{
        datasets: {
          [id: string]: Dataset;
        };
        visualizationOrder: string[];
        rawDatasets: {
          [id: string]: DatasetData;
        };
      }>
    ) => {
      state.history = action.payload;
    },
    addFilter: (
      state,
      action: PayloadAction<{
        datasetId: string;
        filter: DatasetFilter[];
      }>
    ) => {
      const { datasetId, filter } = action.payload;

      if (!state.filters[datasetId]) state.filters[datasetId] = [];

      state.filters[datasetId] = filter;
    },
    removeFilter: (
      state,
      action: PayloadAction<{ datasetId: string; filterId: string }>
    ) => {
      const { datasetId, filterId } = action.payload;
      if (state.filters[datasetId]) {
        state.filters[datasetId] = state.filters[datasetId].filter(
          (f) => f.filterId !== filterId
        );
      }
    },
    updateFilters: (
      state,
      action: PayloadAction<{
        datasetId: string;
        filters: DatasetFilter[];
      }>
    ) => {
      const { datasetId, filters } = action.payload;
      state.filters[datasetId] = filters;
    },
    clearFilters: (state, action: PayloadAction<string>) => {
      const datasetId = action.payload;
      if (state.filters[datasetId]) {
        state.filters[datasetId] = [];
      }
    },
    clearAllFilters: (state) => {
      state.filters = {};
    },
    reorderDatasets: (state, action: PayloadAction<string[]>) => {
      state.visualizationOrder = action.payload;
    },

    toggleDatasetVisibility: (
      state,
      action: PayloadAction<{ datasetId: string; visibility?: boolean }>
    ) => {
      const { datasetId, visibility } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) {
        const newVisibility = visibility ?? !dataset.properties.isVisible;

        dataset.properties.isVisible = newVisibility;
        dataset.style.visibility = newVisibility ? "visible" : "none";
      }
    },
    updateDatasetSelectedCategory: (
      state,
      action: PayloadAction<{
        datasetId: string;
        selectedCategoryName: string;
      }>
    ) => {
      const { datasetId, selectedCategoryName } = action.payload;
      const selectedCategory = state.datasets[datasetId].categories.find(
        (category) => category.attribute_name === selectedCategoryName
      );

      if (state.datasets[datasetId] && selectedCategory) {
        state.datasets[datasetId].selectedCategory = selectedCategory;
      }
    },
    resetDatasetToHistoryDataset: (state) => {
      state.datasets = state.history.datasets;
      state.visualizationOrder = state.history.visualizationOrder;
      state.rawDatasets = state.history.rawDatasets;

      state.history = {
        datasets: {},
        visualizationOrder: [],
        rawDatasets: {}
      };
    },
    hideAllDatasetVisibility: (state) => {
      Object.keys(state.datasets).forEach((key) => {
        state.datasets[key].properties.isVisible = false;
      });
    },
    incrementDatasetCount: (
      state,
      action: PayloadAction<{ baseId: string; usedIndex: number }>
    ) => {
      const { baseId, usedIndex } = action.payload;

      state.datasetIndices = {
        ...state.datasetIndices,
        [baseId]: {
          counter: (state.datasetIndices[baseId]?.counter || 0) + 1,
          usedIndices: [
            ...(state.datasetIndices[baseId]?.usedIndices || []),
            usedIndex
          ]
        }
      };
    },

    updateDatasetOpacity: (
      state,
      action: PayloadAction<{ datasetId: string; opacity: number }>
    ) => {
      const { datasetId, opacity } = action.payload;
      if (state.datasets[datasetId]) {
        const style = state.datasets[datasetId].style;
        style.fillOpacity = opacity;
        rememberColorScaleSettings(style, style.colorScaleMethod, { opacity });
      }
    },
    updateDatasetStrokeWidth: (
      state,
      action: PayloadAction<{ datasetId: string; width: number }>
    ) => {
      const { datasetId, width } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.stroke.width = width;
      }
    },
    updateDatasetStrokeOpacity: (
      state,
      action: PayloadAction<{ datasetId: string; opacity: number }>
    ) => {
      const { datasetId, opacity } = action.payload;
      if (state.datasets[datasetId]) {
        const style = state.datasets[datasetId].style;
        style.stroke.opacity = opacity;
        rememberColorScaleSettings(style, style.colorScaleMethod, { opacity });
      }
    },
    updateDatasetStrokeAttribute: (
      state,
      action: PayloadAction<{
        datasetId: string;
        attributeId: string;
        attributeName: string;
      }>
    ) => {
      const { datasetId, attributeId, attributeName } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.stroke.attribute_id = attributeId;
        state.datasets[datasetId].style.stroke.attribute_name = attributeName;
      }
    },
    cleanupVisualizationOrder: (state) => {
      // Remove any IDs from visualizationOrder that don't exist in datasets
      state.visualizationOrder = state.visualizationOrder.filter(
        (id) => id in state.datasets
      );

      // Add any datasets that are missing from visualizationOrder
      const missingIds = Object.keys(state.datasets).filter(
        (id) => !state.visualizationOrder.includes(id)
      );

      if (missingIds.length > 0) {
        state.visualizationOrder = [...state.visualizationOrder, ...missingIds];
      }
    },
    setWeightValuesForDatasetId: (
      state,
      action: PayloadAction<{
        datasetId: string;
        data: {
          weight: string;
          include_scoring?: boolean;
          attributes: Record<string, WeightValueEntry>;
        };
      }>
    ) => {
      const { datasetId, data } = action.payload;

      state.weightValues[datasetId] = data;
    },
    /** Action to assign initial weight values for dataset attributes that are eligible to be assigned a weight value */
    initializeWeightValues: (state) => {
      performRecalculateAllPercentagesV2(state);
    },
    /** Action to calculate percentages from existing weight values loaded from configuration.
     * Unlike initializeWeightValues, this does NOT reset/modify weights - only computes percentages. */
    recalculateWeightPercentages: (state) => {
      performCalculatePercentagesFromExistingWeights(state);
    },
    saveWeightValues: (
      state,
      action: PayloadAction<{
        weightValues: WeightValues;
      }>
    ) => {
      // Save all weight values at once
      state.weightValues = action.payload.weightValues;
      state.weightConfigBaseline = Object.keys(action.payload.weightValues);
    },
    /** Seed the saved-weight baseline when a saved project/analysis config is
     * loaded, without replacing the already-restored weight values. */
    captureWeightConfigBaseline: (state) => {
      state.weightConfigBaseline = Object.keys(state.weightValues);
    },
    setDatasetState: (_, action: PayloadAction<DatasetState>) => {
      return action.payload;
    },
    setSelectedDatasetExplorer: (
      state,
      action: PayloadAction<DatasetExploreItem | null>
    ) => {
      state.selectedDatasetExplorer = action.payload;
    },
    updateDatasetCategories: (
      state,
      action: PayloadAction<{
        datasetId: string;
        categories: Dataset["categories"];
      }>
    ) => {
      const { datasetId, categories } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].categories = categories;
      }
    },
    updateDatasetGroupBy: (
      state,
      action: PayloadAction<{ datasetId: string; groupBy: string }>
    ) => {
      const { datasetId, groupBy } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].group_by = groupBy;
      }
    },
    toggleUseGlobalFilter: (
      state,
      action: PayloadAction<{
        datasetId: string;
        useGlobalFilter: boolean;
      }>
    ) => {
      const { datasetId, useGlobalFilter } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].is_global_admin_area = useGlobalFilter;
      }
    },
    clearAllGlobalFilterLinks: (state) => {
      Object.keys(state.datasets).forEach((id) => {
        if (state.datasets[id].is_global_admin_area) {
          state.datasets[id].is_global_admin_area = false;
        }
      });
    },
    resetDatasetState: () => datasetInitialState
  }
});

export const {
  addDataset,
  addRawDataset,
  removeRawDataset,
  clearRawDatasets,
  updateDatasetStyle,
  updateDatasetColorStyle,
  updateDatasetColorScaleMethod,
  updateDatasetColorScaleSteps,
  previewDatasetSingleStyleConfig,
  commitDatasetSingleStyleConfig,
  cancelDatasetSingleStyleConfig,
  previewDatasetCustomCategoryConfig,
  commitDatasetCustomCategoryConfig,
  seedDatasetCustomCategoryConfig,
  syncDatasetCustomCategoryConfig,
  cancelDatasetCustomCategoryConfig,
  previewDatasetCustomRangeConfig,
  commitDatasetCustomRangeConfig,
  cancelDatasetCustomRangeConfig,
  applyLegendImageAssetIds,
  updateDatasetSelectedCategory,
  updateRawDatasetDisplayName,
  updateDatasetDisplayName,
  updateDatasetStyleVisibleLabel,
  updateDatasetStyleAttributeLabel,
  updateDatasetStyleColorLabel,
  updateDatasetStyleOpacityLabel,
  removeDataset,
  setSelectedDataset,
  clearSelectedDataset,
  addFilter,
  clearDataset,
  removeFilter,
  updateFilters,
  clearAllFilters,
  clearFilters,
  reorderDatasets,
  toggleDatasetVisibility,
  incrementDatasetCount,
  cleanupVisualizationOrder,
  addAdministrativeData,
  setDatasetState,
  setAnalysisId,
  setDatasetIsFetching,
  resetDatasetState,
  updateDatasetOpacity,
  setDatasetHistory,
  resetDatasetToHistoryDataset,
  hideAllDatasetVisibility,
  initializeWeightValues,
  saveWeightValues,
  captureWeightConfigBaseline,
  setWeightValuesForDatasetId,
  recalculateWeightPercentages,
  updateDatasetStrokeWidth,
  updateDatasetStrokeOpacity,
  updateDatasetStrokeAttribute,
  updateDatasetPublishedStatus,
  updateDatasetH3Config,
  setSelectedDatasetExplorer,
  updateDatasetStyleMaxRanking,
  updateDatasetStyleEnabledRank,
  updateDatasetCategories,
  updateDatasetGroupBy,
  toggleUseGlobalFilter,
  clearAllGlobalFilterLinks
} = datasetSlice.actions;

export const datasetReducer = datasetSlice.reducer;
